import { useCallback, useMemo, useState } from 'react';

const getDefault = (id: string) => {
  const [, type = ''] = id.split('_');

  switch (type) {
    case 'segments':
    case 'lookalike':
    case 'demographics':
    case 'location':
      return false;

    default:
      return true;
  }
};

type UseToggleStateReturn = {
  toggle: (id?: string) => () => void;
  isExpanded: (id?: string) => boolean;
  setExpanded: (id: string | undefined, expanded: boolean) => void;
  copyState: (fromUuid: string, toUuid: string) => void;
};

export const useToggleState = (
  initialState?: Map<string, boolean> | (() => Map<string, boolean>)
): UseToggleStateReturn => {
  const [state, setState] = useState<Map<string, boolean>>(() => {
    if (typeof initialState === 'function') {
      return initialState();
    }

    return initialState ?? new Map<string, boolean>();
  });

  const toggle = useCallback(
    (id?: string) => () => {
      if (!id) {
        return;
      }

      setState(prevState => new Map(prevState.set(id, !(prevState.get(id) ?? getDefault(id)))));
    },
    []
  );

  const isExpanded = useCallback(
    (id?: string) => {
      if (!id) {
        return true;
      }

      return state.get(id) ?? getDefault(id);
    },
    [state]
  );

  const setExpanded = useCallback((id: string | undefined, expanded: boolean) => {
    if (!id) {
      return;
    }

    setState(prevState => new Map(prevState.set(id, expanded)));
  }, []);

  const copyState = useCallback((fromUuid: string, toUuid: string) => {
    setState(prevState => {
      const newState = new Map(prevState);
      const prefix = `${fromUuid}_`;

      prevState.forEach((value, key) => {
        if (key.startsWith(prefix)) {
          newState.set(`${toUuid}_${key.slice(prefix.length)}`, value);
        }
      });

      return newState;
    });
  }, []);

  return useMemo(
    () => ({
      toggle,
      isExpanded,
      setExpanded,
      copyState,
    }),
    [toggle, isExpanded, setExpanded, copyState]
  );
};
